import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img/Img";
import { Button, GridContainer, GridItem, Link } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser"
import * as React from "react";
import styled, { css } from "styled-components";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import translate from "@insite/client-framework/Translate";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import useWindowSize from "@DSG/Common/Hooks/useWindowSize";

type Props = OwnProps & HasProduct;
// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    useImageAsBackgroundForColumn1 = "useImageAsBackgroundForColumn1",
    backgroundAsColorColumn1 = "backgroundAsColorColumn1",
    backgroundAsImageDesktopColumn1 = "backgroundAsImageDesktopColumn1",
    backgroundAsImageMobileColumn1 = "backgroundAsImageMobileColumn1",
    showContentOverBackgroundColumn1 = "showContentOverBackgroundColumn1",
    backgroundTextContentColumn1 = "backgroundTextContentColumn1",
    imageAltTextForColumn1 = "imageAltTextForColumn1", 

    useImageAsBackgroundForColumn2 = "useImageAsBackgroundForColumn2",
    backgroundAsColorColumn2 = "backgroundAsColorColumn2",
    backgroundAsImageDesktopColumn2 = "backgroundAsImageDesktopColumn2",
    backgroundAsImageMobileColumn2 = "backgroundAsImageMobileColumn2",
    showContentOverBackgroundColumn2 = "showContentOverBackgroundColumn2",
    backgroundTextContentColumn2 = "backgroundTextContentColumn2",
    imageAltTextForColumn2 = "imageAltTextForColumn2", 
}

interface OwnProps extends WidgetProps  {
    fields: {
        [fields.useImageAsBackgroundForColumn1]: boolean;
        [fields.showContentOverBackgroundColumn1]: boolean;
        [fields.backgroundAsColorColumn1]: string;
        [fields.backgroundAsImageDesktopColumn1]: string;
        [fields.backgroundAsImageMobileColumn1]: string;
        [fields.backgroundTextContentColumn1]: string;
        [fields.imageAltTextForColumn1]: string;

        [fields.useImageAsBackgroundForColumn2]: boolean;
        [fields.showContentOverBackgroundColumn2]: boolean;
        [fields.backgroundAsColorColumn2]: string;
        [fields.backgroundAsImageDesktopColumn2]: string;
        [fields.backgroundAsImageMobileColumn2]: string;
        [fields.backgroundTextContentColumn2]: string;
        [fields.imageAltTextForColumn2]: string;
    };
}

export interface DsgTwoColumnCommonWidgetStyles {
    wrapper?: InjectableCss;
    boxGridContainer?: GridContainerProps;
    boxGridItem?: GridItemProps;
    imgWrapper?: InjectableCss;
    headingText?: InjectableCss;
    subHeadingText?: InjectableCss;
    contentWrapper?: InjectableCss;
    linkText?: InjectableCss;
    linkWrapper?: InjectableCss;
    columnGridContainer?: GridContainerProps;
    columnGridItem?: GridItemProps;
}


export const dsgTwoColumnCommonWidgetStyles: DsgTwoColumnCommonWidgetStyles = {
    columnGridContainer: {
        gap: 0,
        css: css`
            > div {
                row-gap: 8px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            > div {
                                > div {
                                    &:first-child {
                                        padding-right: 8px;
                                    }
                                    &:last-child {
                                        padding-left: 8px;
                                    }
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `
    },
    columnGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            position: relative;
            border-radius: 8px;
            > div {
                width: 100%;
                height: 100%;
                display: flex;
            }
            .richContentWrapper [class*="TypographyStyle"],
            h1 {
                font: ${headlineFonts.headline1SemiBoldSm.fontWeight} ${headlineFonts.headline1SemiBoldSm.fontSize}/${headlineFonts.headline1SemiBoldSm.lineHeight} ${headlineFonts.headline1SemiBoldSm.fontFamily}  !important;
                color: ${colorPallete.white} !important;
                margin-bottom: 0;
            }
            .richContentWrapper {
                height: 100%;
                padding: 16px;
                border-radius: 8px;
                flex-direction: column;
                row-gap: 16px;
                background-size: cover;
                background-repeat: no-repeat;
                min-height: 140px;
                &.contentWithBg {
                    padding: 0;
                    > img {
                        width: 100%;
                        object-fit: cover;
                        border-radius: 8px
                    }
                    .contentOnOverlay {
                        position: absolute;
                        top: 16px;
                        left: 16px;
                        right: 16px;
                        width: auto;
                    }
                }
                [class*="LazyImageStyle"] {
                    height: 40px;
                    max-height: 40px;
                    overflow: visible;
                    align-items: flex-start;
                    img {
                        width: auto;
                        max-height: 40px;
                    }
                }
            }
            .imageWrapper {
                img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain; 
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .richContentWrapper {
                                row-gap: 24px;
                                [class*="LazyImageStyle"] {
                                    max-height: 48px;
                                    height: 48px;
                                    img {
                                        max-height: 48px;
                                    }
                                }
                            }
                        `,
                        css`
                            .richContentWrapper {
                                padding: 40px;
                                &.contentWithBg {
                                    padding: 0;
                                    .contentOnOverlay {
                                        top: 40px;
                                        left: 40px;
                                        right: 40px;
                                    }
                                }
                            }
                            .richContentWrapper [class*="TypographyStyle"],
                            h1 {
                                font-size: ${headlineFonts.headline1SemiBoldLg.fontSize} !important;
                                line-height: ${headlineFonts.headline1SemiBoldLg.lineHeight} !important;
                            }
                        `,
                    ],
                    "min",
                )}
        `
    },
    wrapper: {
        css: css`
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-bottom: 40px;
                    `,
                ],
                "min",
            )}
        `
    }
}

const styles = dsgTwoColumnCommonWidgetStyles;

const DsgTwoColumnCommonWidget: React.FC<Props> = props => {
    const { width } = useWindowSize();
    let column1BgImage, column2BgImage;
    if(props.fields.useImageAsBackgroundForColumn1 || props.fields.useImageAsBackgroundForColumn2) {
        if(width > 768) {
            column1BgImage = props.fields.backgroundAsImageDesktopColumn1;
            column2BgImage = props.fields.backgroundAsImageDesktopColumn2;
        } else {
            column1BgImage = props.fields.backgroundAsImageMobileColumn1;
            column2BgImage = props.fields.backgroundAsImageMobileColumn2;
        }
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.columnGridContainer}>
                <GridItem {...styles.columnGridItem}>
                    <StyledWrapper className={`richContentWrapper ${props.fields.useImageAsBackgroundForColumn1 ? 'contentWithBg' : ''}`}
                        style={!props.fields.useImageAsBackgroundForColumn1 ? 
                                    {background: props.fields.backgroundAsColorColumn1} : {}}>
                        {props.fields.showContentOverBackgroundColumn1 && (
                            <StyledWrapper className="contentOnOverlay">
                                {parse(props.fields.backgroundTextContentColumn1, parserOptions)}
                            </StyledWrapper>
                        )}
                        {props.fields.useImageAsBackgroundForColumn1 && (
                            <Img src={column1BgImage} altText={props.fields.imageAltTextForColumn1} />
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.columnGridItem}>
                    <StyledWrapper className={`richContentWrapper ${props.fields.useImageAsBackgroundForColumn2 ? 'contentWithBg' : ''}`}
                        style={!props.fields.useImageAsBackgroundForColumn2 ? 
                                    {background: props.fields.backgroundAsColorColumn2} : {}}>
                        {props.fields.showContentOverBackgroundColumn2 && (
                            <StyledWrapper className="contentOnOverlay">
                                {parse(props.fields.backgroundTextContentColumn2, parserOptions)}
                            </StyledWrapper>
                        )}
                        {props.fields.useImageAsBackgroundForColumn2 && (
                            <Img src={column2BgImage} altText={props.fields.imageAltTextForColumn2} />
                        )}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};



const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.useImageAsBackgroundForColumn1,
            displayName: "Use Background As a Image For Column 1",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            sortOrder: 1,
        },
        {
            fieldType: "Contextual",
            displayName: "Background As Color For Column 1",
            name: fields.backgroundAsColorColumn1,
            editorTemplate: "ColorPickerField",
            defaultValue: "#C91E24",
            sortOrder: 2,
            isVisible: widget => !widget.fields.useImageAsBackgroundForColumn1,
        },
        {
            fieldType: "Contextual",
            displayName: "Background As Image Desktop View Column 1",
            name: fields.backgroundAsImageDesktopColumn1,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 3,
            isVisible: widget => widget.fields.useImageAsBackgroundForColumn1,
        },
        {
            fieldType: "Contextual",
            displayName: "Background As Image Mobile View Column 1",
            name: fields.backgroundAsImageMobileColumn1,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 4,
            isVisible: widget => widget.fields.useImageAsBackgroundForColumn1,
        },
        {
            fieldType: "General",
            displayName: "Image Alt Text For Column1",
            name: fields.imageAltTextForColumn1,
            editorTemplate: "TextField",
            defaultValue: "Column Banner Image",
            sortOrder: 4,
            isVisible: widget => widget.fields.useImageAsBackgroundForColumn1,
        },
        {
            name: fields.showContentOverBackgroundColumn1,
            displayName: "Show Content Over Background For Column 1",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 5,
        },
        {
            fieldType: "Contextual",
            name: fields.backgroundTextContentColumn1,
            editorTemplate: "RichTextField",
            displayName: "Background Text Content For Column 1",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            sortOrder: 6,
            isVisible: widget => widget.fields.showContentOverBackgroundColumn1,
        },
       
        {
            name: fields.useImageAsBackgroundForColumn2,
            displayName: "Use Background As a Image For Column 2",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 7,
        },
        {
            fieldType: "Contextual",
            displayName: "Background As Color For Column 2",
            name: fields.backgroundAsColorColumn2,
            editorTemplate: "ColorPickerField",
            defaultValue: "#C91E24",
            sortOrder: 8,
            isVisible: widget => !widget.fields.useImageAsBackgroundForColumn2,
        },
        {
            fieldType: "Contextual",
            displayName: "Background As Image Desktop View Column 2",
            name: fields.backgroundAsImageDesktopColumn2,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 9,
            isVisible: widget => widget.fields.useImageAsBackgroundForColumn2,
        },
        {
            fieldType: "Contextual",
            displayName: "Background As Image Mobile View Column 2",
            name: fields.backgroundAsImageMobileColumn2,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 10,
            isVisible: widget => widget.fields.useImageAsBackgroundForColumn2,
        },
        {
            fieldType: "General",
            displayName: "Image Alt Text For Column2",
            name: fields.imageAltTextForColumn2,
            editorTemplate: "TextField",
            defaultValue: "Column Banner Image",
            sortOrder: 10,
            isVisible: widget => widget.fields.useImageAsBackgroundForColumn2,
        },
        {
            name: fields.showContentOverBackgroundColumn2,
            displayName: "Show Content Over Background For Column 2",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            sortOrder: 11,
        },
        {
            fieldType: "Contextual",
            name: fields.backgroundTextContentColumn2,
            editorTemplate: "RichTextField",
            displayName: "Background Text Content For Column 2",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            sortOrder: 12,
            isVisible: widget => widget.fields.showContentOverBackgroundColumn2,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withProduct(DsgTwoColumnCommonWidget),
    definition,
};

export default widgetModule;
